import PropTypes from 'prop-types';
import React from 'react';
import { WeezHeader, Dropdown, SelectButton, Icon, Numbers, ToastContext } from '@weezevent/nacre';

import { useHistory } from 'react-router-dom';

import Loading from '../../components/loading';

import { useTranslation } from 'react-i18next';
import RatesTable from './ratesTable';
import RateTemplate from '../../models/rateTemplate';
import RateTemplateGroup from '../../models/rateTemplateGroup';
import Chart from '../../models/chart';
import Category from '../../models/category';
import EventModel from '../../models/eventModel';
import moment from 'moment-timezone';
import { Card } from '../../components/card';
import { DeleteAction } from './components/action/deleteAction';
import { CategoryAction } from './components/action/categoryAction';
import { GroupAction } from './components/action/groupAction';
import { LinkEventAction } from './components/action/linkEventAction';
import { ModifyAction } from './components/action/modifyAction';
import { NewGroupAction } from './components/action/newGroupAction';
import { RateCategorySeat } from './components/rateCategorySeat';
import { generateClassNameRateName } from './components/rateManager';
import StyleRM from './components/rateManager.module.css';
import i18n from 'i18next';
import { compute_sum_float } from '../../pages/rates/utils';

export default function RatesTemplate({ match, organization }) {
    const { t } = useTranslation();
    const [rates, setRates] = React.useState([]);
    const [groups, setGroups] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [events, setEvents] = React.useState([]);
    const [selectedRates, setSelected] = React.useState([]);
    const [selectedGroups, setSelectedGroups] = React.useState([]);
    const [allSelected, setAllSelected] = React.useState(false);
    const [orgaWithChart, setOrgaWithChart] = React.useState(false);

    const [isLoading, setLoading] = React.useState(true);
    const toast = React.useContext(ToastContext);
    const history = useHistory();

    const organizationId = organization.id;

    const categoriesList = React.useMemo(() => {
        if (!categories) {
            return [];
        }

        const categoriesListTmp = [];
        categories.forEach(chart => {
            if (!chart.categories) {
                return;
            }
            categoriesListTmp.push(...chart.categories);
        });

        return categoriesListTmp;
    }, [categories]);

    let tableCells = [
        {
            header: t('ticket.rates.rateName'),
            renderRate: rate => {
                return (
                    <div className={StyleRM[generateClassNameRateName(rate, groups)]} style={{ display: 'flex', alignItems: 'center' }}>
                        <RateCategorySeat rate={rate} categories={categoriesList} />
                        <span>{rate.name}</span>
                    </div>
                );
            },
            renderGroup: (group, onToggle) => {
                return (
                    <div className={StyleRM['rates-table__cell-group']} onClick={() => onToggle(group)}>
                        {group.parent && <Icon className={StyleRM['rates-table__cell-group-icon-subgroup']} name="link-group" />}
                        <Icon name={group.showRates ? 'folder-open' : 'folder-closed'} style={{ marginRight: '5px' }} />
                        <b>{group.name}</b>
                    </div>
                );
            }
        },
        {
            header: t('ticket.rates.price'),
            renderRate: rate => {
                const computedPrice = rate.fees?.length && rate.fees.reduce((sum, { amount }) => compute_sum_float(sum, parseFloat(amount) || 0), parseFloat(rate.price || 0));
                return rate.price !== '0.00' ? (
                    <Numbers value={computedPrice || rate.price} locale={i18n.language} currency={organization.currency} showNullDecimal />
                ) : (
                    t('ticket.rates.free')
                );
            },
            renderGroup: () => {
                return null;
            }
        },
        {
            header: t('ticket.rates.quota'),
            renderRate: (rate, { event }) => {
                if (rate.quantity === 0) {
                    return '-';
                }
                return rate.quantity;
            },
            renderGroup: group => {
                // group.quota being unlimited is now at none, not zero, but values have not been updated
                // So some unlimited quota are still recognized by 0 value, until they are saved and updated
                // TODO : clean group.quota === 0 once this value is no more used
                if (!group.quota || group.quota === 0) {
                    return '-';
                }
                return group.quota;
            }
        }
    ];

    React.useEffect(() => {
        let eventElements = EventModel.getAll({ organizationId });
        Promise.all([eventElements])
            .then(([eventElements]) => {
                let eventsTmp = eventElements.filter(event => {
                    if (event.publication_status === 'closed') {
                        return false;
                    }

                    if (event.end_date && event.end_date < moment.utc().format()) {
                        return false;
                    }

                    return true;
                });
                setEvents(eventsTmp.sort((el1, el2) => new Date(el2.start_date).getTime() - new Date(el1.start_date).getTime()));
            })
            .catch(e => {
                console.error(e);
                toast.error(t('ticket.common.toast_error'));
            });
    }, []);

    const reloadList = React.useCallback(() => {
        setLoading(true);
        let rateElements = RateTemplate.getAll({
            organizationId,
            query: {
                include_events: true
            }
        });
        let rateGroups = RateTemplateGroup.getAll({
            organizationId
        });
        let chartPromise = Chart.getAll({ organizationId, query: { only_seated: true } });

        Promise.all([rateElements, rateGroups, chartPromise])
            .then(([rateElements, ratesGroup, charts]) => {
                //Sort rate and group with display_order
                RateTemplate.sort(rateElements);
                RateTemplateGroup.sort(ratesGroup);
                setRates(rateElements);
                ratesGroup.forEach(group => (group.showRates = true));
                setGroups(ratesGroup);
                let categoriesPromises = [];
                setOrgaWithChart(charts.length > 0);

                charts.forEach(chart => {
                    let categoryPromise = Category.getAll({
                        organizationId,
                        chartId: chart.pk
                    });
                    categoriesPromises.push(categoryPromise);
                });
                return Promise.all([charts, Promise.all(categoriesPromises)]);
            })
            .then(([charts, categories]) => {
                let tmpCategories = [];
                for (let i = 0; i < categories.length; i++) {
                    if (!categories[i].length) {
                        continue;
                    }
                    let tmpChart = {
                        chart: charts[i].name,
                        categories: categories[i]
                    };
                    tmpCategories.push(tmpChart);
                }
                setCategories(tmpCategories);
            })
            .finally(() => {
                setLoading(false);
            })
            .catch(e => {
                toast.error(t('ticket.common.toast_error'));
            });
        setSelected([]);
        setSelectedGroups([]);
        setAllSelected(false);
    }, [organizationId]);

    React.useEffect(() => {
        reloadList();
    }, []);

    const selectRate = React.useCallback(
        rate => {
            let newSelected = selectedRates.slice(0);
            if (newSelected.find(selected => selected.pk === rate.pk)) {
                return;
            }
            newSelected.push(rate);

            //If a rate is selected, we check if every other rates of its group are checked to check the group as well
            if (rate.group) {
                //Check if the number of rates selected with the current rate group is equal to the number of every rates with this group
                let selectedRatesLength = newSelected.filter(frate => frate.group === rate.group).length;
                let ratesLength = rates.filter(frate => frate.group === rate.group).length;
                if (selectedRatesLength === ratesLength) {
                    let tmpGroup = groups.find(group => group.pk === rate.group);
                    let newSelectedGroups = Array.from(selectedGroups);
                    newSelectedGroups.push(tmpGroup);
                    setSelectedGroups(newSelectedGroups);
                }
            }

            if ((rate.group || selectedGroups.length === groups.length) && newSelected.length === rates.length && rates.length > 0) {
                setAllSelected(true);
            }
            return setSelected(newSelected);
        },
        [selectedRates, selectedGroups, groups]
    );

    const deselectRate = React.useCallback(
        rate => {
            if (allSelected) {
                setAllSelected(false);
            }
            let newSelected = selectedRates.slice(0);
            let selectedIndex = selectedRates.findIndex(selement => selement.pk === rate.pk);
            newSelected.splice(selectedIndex, 1);

            //If a rate is unchecked, we look if its group is checked, we uncheck it
            if (rate.group) {
                //Check if the number of rates selected with the current rate group is equal to the number of every rates with this group
                let selectedRatesLength = newSelected.filter(frate => frate.group === rate.group).length + 1;
                let ratesLength = rates.filter(frate => frate.group === rate.group).length;
                if (selectedRatesLength === ratesLength) {
                    let newSelectedGroups = selectedGroups.slice(0);
                    let selectedIndexGroup = selectedGroups.findIndex(sgroup => sgroup.pk === rate.group);
                    newSelectedGroups.splice(selectedIndexGroup, 1);
                    setSelectedGroups(newSelectedGroups);
                }
            }
            return setSelected(newSelected);
        },
        [allSelected, selectedRates, rates, selectedGroups]
    );

    const selectGroup = React.useCallback(
        group => {
            let newSelected = selectedGroups.slice(0);
            if (newSelected.find(selected => selected.pk === group.pk)) {
                return;
            }
            newSelected.push(group);
            // we check every rates of its group
            let ratesFromGroup = rates.filter(rate => rate.group === group.pk);
            let tmpSelectedRates = Array.from(selectedRates);
            tmpSelectedRates.push(...ratesFromGroup);
            tmpSelectedRates = Array.from(new Set(tmpSelectedRates));
            setSelected(tmpSelectedRates);
            if (newSelected.length === groups.length && tmpSelectedRates.length === rates.length && rates.length > 0) {
                setAllSelected(true);
            }
            return setSelectedGroups(newSelected);
        },
        [selectedGroups, rates, selectedRates, groups]
    );

    const deselectGroup = React.useCallback(
        group => {
            if (allSelected) {
                setAllSelected(false);
            }
            let newSelected = selectedGroups.slice(0);
            let selectedIndex = selectedGroups.findIndex(selement => selement.pk === group.pk);
            newSelected.splice(selectedIndex, 1);
            //We uncheck every rate of the group
            let ratesFromGroup = rates.filter(rate => rate.group === group.pk);
            ratesFromGroup = ratesFromGroup.map(rate => rate.pk);
            let tmpSelectedRates = selectedRates.filter(rate => !ratesFromGroup.includes(rate.pk));
            setSelected(tmpSelectedRates);
            return setSelectedGroups(newSelected);
        },
        [allSelected, selectedGroups, rates, selectedRates]
    );

    const handleAllClick = React.useCallback(
        ({ event, data: { checked } }) => {
            setAllSelected(!allSelected);
            if (checked) {
                setSelected([...rates]);
                setSelectedGroups([...groups]);
            } else {
                setSelected([]);
                setSelectedGroups([]);
            }
        },
        [allSelected, rates, groups]
    );

    // Handle click./check with shift key
    const handleElementsList = React.useCallback(
        ({ event, data: { checked } }, element, type) => {
            event.stopPropagation();
            let tmpArray = type === 'rate' ? selectedRates : selectedGroups;
            const selected = tmpArray.findIndex(selement => selement.pk === element.pk) > -1;
            if ((selected && checked) || (!selected && !checked)) {
                return;
            }

            let newSelected = tmpArray.slice(0);

            if (checked && !selected) {
                if (event.shiftKey) {
                    let [lastSelected] = tmpArray.slice(-1);
                    let lastSelectedIndex = tmpArray.findIndex(felement => felement.pk === lastSelected.pk);
                    let selectionIndex = tmpArray.findIndex(felement => felement.pk === element.pk);

                    if (selectionIndex < lastSelectedIndex) {
                        [selectionIndex, lastSelectedIndex] = [lastSelectedIndex, selectionIndex];
                    }

                    newSelected.push(...selectedRates.slice(lastSelectedIndex, selectionIndex + 1));

                    newSelected = Array.from(new Set(newSelected));
                } else {
                    return type === 'rate' ? selectRate(element) : selectGroup(element);
                }
            }

            if (!checked && selected) {
                return type === 'rate' ? deselectRate(element) : deselectGroup(element);
            }
            return type === 'rate' ? setSelected(newSelected) : setSelectedGroups(newSelected);
        },
        [selectedRates, selectedGroups, rates]
    );

    if (isLoading) {
        return <Loading />;
    }
    return (
        <>
            <WeezHeader
                title={t('ticket.rates.template.pageTitle')}
                dropdownProps={{
                    renderWhenClosed: true
                }}
                dropdown={[
                    <Dropdown.Item key={0} item={t('ticket.rates.addRate')} onClick={() => history.push(`/ticket/O${organizationId}/rates/new`)} />,
                    <NewGroupAction key={1} groups={groups} model={RateTemplateGroup} onSave={() => reloadList()} />
                ]}
            />
            <Card
                title={t('ticket.rates.cardRateTitle')}
                subtitle={t('ticket.rates.cardRateSubtitle', {
                    count: rates?.length
                })}
            >
                <SelectButton
                    tooltipTitle={t('ticket.rates.noActionTooltip')}
                    title={selectedRates.length > 0 ? t('ticket.rates.rateAction', { count: selectedRates.length }) : t('ticket.common.action.bulk')}
                    objects={selectedRates}
                >
                    <ModifyAction />
                    <GroupAction groups={groups} rates={rates} model={RateTemplate} onSave={() => reloadList()} />
                    <LinkEventAction events={events} reloadList={reloadList} />
                    {orgaWithChart && <CategoryAction categories={categories.map(chart => chart.categories).flat(Infinity)} model={RateTemplate} onSave={() => reloadList()} />}
                    <Dropdown.Line />
                    <DeleteAction
                        rates={rates}
                        model={RateTemplate}
                        disabled={selectedRates.length !== 1}
                        onSave={() => {
                            setSelected([]);
                            reloadList();
                        }}
                    />
                </SelectButton>

                <RatesTable
                    t={t}
                    tableCells={tableCells}
                    organizationId={organizationId}
                    groups={groups}
                    GroupClass={RateTemplateGroup}
                    rates={rates}
                    RateClass={RateTemplate}
                    categories={categories.map(chart => chart.categories).flat(Infinity)}
                    reloadList={reloadList}
                    handleElementsList={handleElementsList}
                    selectedGroups={selectedGroups}
                    selectedRates={selectedRates}
                    allSelected={allSelected}
                    orgaWithChart={orgaWithChart}
                    handleAllClick={(event, data) =>
                        handleAllClick({
                            event,
                            data
                        })
                    }
                    onToggleGroup={setGroups}
                    mode="rate-list"
                />
            </Card>
        </>
    );
}

RatesTemplate.propTypes = {
    match: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired
};
